import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { isEngagementDetailsPath } from '../../../util/routeUtils'
import type { PostListItem } from '../types'

type UseAutoSelectPostParams = {
  posts: PostListItem[]
  selectedPostId?: string
  onPostSelect: (postId: string, options: { replace: boolean }) => void
}

/**
 * When no post is selected (no selectedPostId in URL), auto-selects the first
 * post in the list. When selectedPostId is set (e.g. from URL), we do not
 * override it so the user stays on the post they navigated to.
 *
 * Stands down while an engagement-details dialog (comment or mention) is
 * open: selecting would navigate to `/community/by-post/…`, unmounting the
 * dialog route out from under the actor.
 */
export function useAutoSelectPost({
  posts,
  selectedPostId,
  onPostSelect,
}: UseAutoSelectPostParams): void {
  const location = useLocation()
  const isDetailsDialogOpen = isEngagementDetailsPath(location.pathname)

  useEffect(() => {
    if (posts.length === 0) return
    if (selectedPostId) return
    if (isDetailsDialogOpen) return

    // Replace, not push: this is URL normalization (defaulting to the first
    // post), not a user action. Pushing would trap Back behind a history
    // entry that immediately re-triggers this same auto-select.
    onPostSelect(posts[0].resolved.id, { replace: true })
  }, [posts, selectedPostId, onPostSelect, isDetailsDialogOpen])
}
